/**
 * product-drawer.js — Tiroirs <dialog class="mb-drawer"> de la fiche produit.
 *
 * Coque : snippets/product-drawer.liquid (tiroir droit en desktop, plein écran en mobile).
 * Ouverture : [data-drawer-open="<id du dialog>"]. Fermeture : [data-drawer-close], Esc, voile.
 * Verrou de scroll iOS-safe : le tiroir est plein écran en mobile.
 *
 * [data-drawer-dismiss] ferme le tiroir en phase de CAPTURE, donc avant l'écouteur qui ouvre la
 * vue suivante (le certificat de product-inspection.js) et déverrouille aussitôt : sinon la vue
 * suivante sauverait la position d'une page encore figée.
 *
 * Esc et voile émettent click_cta `<data-drawer-track>_close_<geste>` ; la croix
 * ([data-cta-name]) est tracée par le délégué de tracking-ecommerce.liquid.
 *
 * Pas de showModal() : il rend toute la page inerte, donc recalcule le style de chaque élément
 * à l'ouverture et à la fermeture (~120 ms par clic mesurés en desktop). Le tiroir s'ouvre en
 * non-modal ; voile, piège à focus et Esc sont gérés ici, aria-modal informe les lecteurs d'écran.
 */
(function () {
  'use strict';

  if (window.__mbDrawerBound) return;
  window.__mbDrawerBound = true;

  var DRAWER = 'mb-drawer';
  var OVERLAY = 'mb-drawer--overlay';
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';

  var savedScrollY = 0;
  var savedBody = null;

  // Barres fixes compensées par la coque (cf. snippets/product-drawer.liquid).
  var GUTTER_TARGETS = '#SiteHeader, .trustpilot-topbar, .footer-price';

  // Lue avant toute écriture de style : lue après, elle forçait un layout complet de la
  // page pendant le clic (~120 ms mesurés, pénalise l'INP).
  function scrollbarWidth() {
    return Math.max(0, window.innerWidth - document.documentElement.clientWidth);
  }

  function setGutterVar(value) {
    var targets = document.querySelectorAll(GUTTER_TARGETS);
    for (var i = 0; i < targets.length; i++) {
      if (value) targets[i].style.setProperty('--mb-drawer-gutter', value);
      else targets[i].style.removeProperty('--mb-drawer-gutter');
    }
  }

  function lockScroll(gutter, scrollY) {
    if (savedBody) return;
    savedScrollY = scrollY;
    var b = document.body;
    savedBody = {
      position: b.style.position,
      top: b.style.top,
      left: b.style.left,
      right: b.style.right,
      width: b.style.width,
      overflow: b.style.overflow,
      paddingRight: b.style.paddingRight,
      gutter: document.documentElement.style.scrollbarGutter,
    };
    b.style.position = 'fixed';
    b.style.top = '-' + savedScrollY + 'px';
    b.style.left = '0';
    b.style.right = '0';
    b.style.width = '100%';
    b.style.overflow = 'hidden';

    // mint.css réserve la gouttière de défilement (scrollbar-gutter: stable) : le tiroir
    // s'arrêterait avant elle (anti-pattern n°50). On la libère et on compense sa largeur.
    var html = document.documentElement;
    html.style.scrollbarGutter = 'auto';
    if (!gutter) return;
    b.style.paddingRight = gutter + 'px';
    // Les barres fixes (en-tête, Trustpilot, prix) s'élargiraient aussi : la coque les compense.
    // Variable posée sur ces barres seulement : sur <html>, tous les éléments en héritent et
    // leur style est recalculé (~50 ms mesurés en desktop à chaque ouverture).
    setGutterVar(gutter + 'px');
    html.setAttribute('data-mb-drawer-gutter', '');
  }

  function unlockScroll() {
    if (!savedBody) return;
    var b = document.body;
    var st = savedBody;
    b.style.position = st.position || '';
    b.style.top = st.top || '';
    b.style.left = st.left || '';
    b.style.right = st.right || '';
    b.style.width = st.width || '';
    b.style.overflow = st.overflow || '';
    b.style.paddingRight = st.paddingRight || '';
    document.documentElement.style.scrollbarGutter = st.gutter || '';
    setGutterVar(null);
    document.documentElement.removeAttribute('data-mb-drawer-gutter');
    savedBody = null;

    // mint.css pose `scroll-behavior: smooth` sur html : sans cette coupure, la remise en place
    // serait animée depuis le haut de page (anti-pattern n°31).
    var html = document.documentElement;
    var previousBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    window.scrollTo(0, savedScrollY);
    html.style.scrollBehavior = previousBehavior;
  }

  // Le geste est dans le nom : la balise GTM click_cta ne transmet pas interaction_label.
  function trackClose(dialog, how) {
    var track = dialog.getAttribute('data-drawer-track');
    if (!track) return;
    var name = track + '_close_' + how;
    var payload =
      typeof window.mbStickyCtaPayload === 'function'
        ? window.mbStickyCtaPayload(name, track)
        : { cta_name: name, cta_location: track };
    if (typeof window.trackEvent === 'function') {
      window.trackEvent('click_cta', payload);
      return;
    }
    window.dataLayer = window.dataLayer || [];
    payload.event = 'click_cta';
    window.dataLayer.push(payload);
  }

  function openDialog(dialog) {
    dialog.classList.add(OVERLAY);
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('open', '');
  }

  function closeDialog(dialog) {
    dialog.removeAttribute('open');
    dialog.removeAttribute('aria-modal');
    dialog.classList.remove(OVERLAY);
  }

  function openDrawerEl() {
    return document.querySelector('.' + OVERLAY + '[open]');
  }

  function focusables(dialog) {
    return Array.prototype.filter.call(dialog.querySelectorAll(FOCUSABLE), function (el) {
      return el.getClientRects().length > 0;
    });
  }

  // Sans preventScroll, le focus fait défiler le dialog vers le titre encore hors champ :
  // le tiroir surgit trop à gauche au lieu de glisser (anti-pattern n°51).
  function focusTitle(dialog) {
    var title = dialog.querySelector('.' + DRAWER + '__title');
    if (!title || typeof title.focus !== 'function') return;
    try {
      title.focus({ preventScroll: true });
    } catch (err) {
      title.focus();
    }
  }

  function openDrawer(dialog, trigger) {
    if (!dialog || dialog.__drawerOpen) return;
    // Lectures groupées avant toute écriture de style (cf. scrollbarWidth).
    var gutter = scrollbarWidth();
    var scrollY = window.scrollY || document.documentElement.scrollTop || 0;
    dialog.__drawerTrigger = trigger;
    // Avant l'affichage : les composants ajustent leur contenu sans flash.
    dialog.dispatchEvent(new CustomEvent('mb-drawer:open', { bubbles: true }));
    openDialog(dialog);
    dialog.__drawerOpen = true;
    lockScroll(gutter, scrollY);
    // Focus après le premier affichage : pendant le clic, il forçait un layout complet de la
    // page tout juste verrouillée (~40 ms mesurés en desktop, ×4 sur mobile, pénalise l'INP).
    requestAnimationFrame(function () {
      setTimeout(function () {
        if (dialog.__drawerOpen) focusTitle(dialog);
      }, 0);
    });
  }

  function closeDrawer(dialog) {
    if (!dialog || !dialog.__drawerOpen) return;
    closeDialog(dialog);
    dialog.__drawerOpen = false;
    unlockScroll();
    var trigger = dialog.__drawerTrigger;
    dialog.__drawerTrigger = null;
    if (trigger && typeof trigger.focus === 'function') {
      try {
        trigger.focus({ preventScroll: true });
      } catch (err) {
        trigger.focus();
      }
    }
  }

  // Capture : fermer avant que la vue suivante s'ouvre (cf. en-tête).
  document.addEventListener(
    'click',
    function (e) {
      if (!e.target || !e.target.closest) return;
      var dismiss = e.target.closest('[data-drawer-dismiss]');
      if (!dismiss) return;
      var dialog = dismiss.closest('.' + DRAWER);
      // La vue suivante rend le focus à ce bouton, caché dans le tiroir fermé.
      dismiss.__returnFocus = dialog && dialog.__drawerTrigger;
      closeDrawer(dialog);
    },
    true,
  );

  // Aucun stopPropagation : le tracking écoute en phase de remontée sur document.
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      var target = document.getElementById(opener.getAttribute('data-drawer-open'));
      if (target && target.classList.contains(DRAWER)) openDrawer(target, opener);
      return;
    }
    var closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      closeDrawer(closer.closest('.' + DRAWER));
      return;
    }
    if (isBackdropClick(e)) {
      trackClose(e.target, 'backdrop');
      closeDrawer(e.target);
    }
  });

  // Voile = clic commencé ET fini sur le <dialog>, hors du panneau : une sélection de
  // texte relâchée sur le voile, ou un clic pendant l'entrée du panneau, ne ferme pas.
  var pointerDownTarget = null;
  document.addEventListener(
    'pointerdown',
    function (e) {
      pointerDownTarget = e.target;
    },
    true,
  );

  function isBackdropClick(e) {
    var dialog = e.target;
    if (!dialog.classList || !dialog.classList.contains(DRAWER)) return false;
    if (pointerDownTarget !== dialog) return false;
    var panel = dialog.querySelector('.' + DRAWER + '__panel');
    if (!panel) return true;
    var rect = panel.getBoundingClientRect();
    return e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom;
  }

  // Esc ferme ; Tab boucle sur les éléments du tiroir (piège à focus du mode non-modal).
  document.addEventListener('keydown', function (e) {
    var dialog = openDrawerEl();
    if (!dialog) return;
    if (e.key === 'Escape') {
      trackClose(dialog, 'escape');
      closeDrawer(dialog);
      return;
    }
    if (e.key !== 'Tab') return;
    var items = focusables(dialog);
    if (!items.length) {
      e.preventDefault();
      focusTitle(dialog);
      return;
    }
    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;
    if (e.shiftKey && (active === first || !dialog.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !dialog.contains(active))) {
      e.preventDefault();
      first.focus();
    }
  });

  // Filet du piège à focus : un focus parti derrière le tiroir (clic, lecteur d'écran) y revient.
  document.addEventListener('focusin', function (e) {
    var dialog = openDrawerEl();
    if (dialog && !dialog.contains(e.target)) focusTitle(dialog);
  });

  // Éditeur de thème : une section re-rendue tiroir ouvert laisserait la page figée.
  document.addEventListener('shopify:section:unload', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('.' + DRAWER + '[open]')) unlockScroll();
  });
})();
